/**
 * Note TOC Button Component
 *
 * IconButton for top bar that opens section list popover on click
 * - Shows TocIcon with "Note Sections" tooltip
 * - Click opens popover (not hover like NoteTOC)
 * - Uses same NoteTOCPopover + NoteTOCSectionList components
 * - Displays on small/medium screens when inline TOC is hidden
 */

import React, { useMemo, useState } from 'react';
import type { NoteSection } from '@features/notes';
import TocIcon from '@mui/icons-material/Toc';
import { IconButton, Tooltip } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useNoteTOC } from '../../hooks';
import { clearCopySectionSelection, toggleCopySectionSelection } from '../../store/chatSlice';
import { selectSelectedSectionIdsForCopy } from '../../store/selectors';

import { NoteTOCPopover } from './NoteTOCPopover';
import { NoteTOCSectionList } from './NoteTOCSectionList';

export interface NoteTOCButtonProps {
  /**
   * Array of note sections to display
   */
  sections: NoteSection[];

  /**
   * CSS selector for the scrollable container containing sections
   */
  containerSelector?: string;

  /**
   * Handler for "Copy Selected" action
   * Typically wired to existing copy functionality
   */
  onCopySelected?: () => void;

  /**
   * Note ID for analytics tracking
   */
  noteId?: string;

  /**
   * Encounter ID for analytics tracking
   */
  encounterId?: string;
}

/**
 * IconButton that opens section list popover
 * Used in top bar on small/medium screens
 */
export const NoteTOCButton: React.FC<NoteTOCButtonProps> = ({
  sections,
  containerSelector,
  onCopySelected,
  noteId,
  encounterId,
}) => {
  const dispatch = useAppDispatch();
  const { noteSection } = useEventTracking();
  const selectedSectionIds = useAppSelector(selectSelectedSectionIdsForCopy);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Convert to TOC format and get active tracking
  const tocSections = useMemo(
    () =>
      sections.map(s => ({
        section_id: s.section_id,
        name: s.name,
      })),
    [sections],
  );

  const { activeSectionId, scrollToSection } = useNoteTOC(tocSections, containerSelector);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Toggle behavior: if popover is open, close it; otherwise open it
    setAnchorEl(prevAnchorEl => (prevAnchorEl ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSectionClick = (sectionId: string) => {
    scrollToSection(sectionId);
    handleClose();
  };

  const handleDeselectAll = () => {
    noteSection.deselectAllClicked({
      source: 'toc',
      selected_count: selectedSectionIds.length,
      note_id: noteId,
      encounter_id: encounterId,
    });
    dispatch(clearCopySectionSelection());
  };

  if (sections.length === 0) {
    return null;
  }

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Note Sections" arrow>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label="note sections"
          sx={{
            color: 'text.secondary',
          }}
        >
          <TocIcon />
        </IconButton>
      </Tooltip>

      <NoteTOCPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <NoteTOCSectionList
          sections={sections}
          activeSectionId={activeSectionId}
          selectedSectionIds={selectedSectionIds}
          onSectionClick={handleSectionClick}
          onToggleSelection={sectionId => dispatch(toggleCopySectionSelection(sectionId))}
          onCopySelected={onCopySelected}
          onDeselectAll={handleDeselectAll}
          noteId={noteId}
          encounterId={encounterId}
          noteSection={noteSection}
        />
      </NoteTOCPopover>
    </>
  );
};
